/**
 * The split between subagents and the rules that keep it honest
 * (VOX-270 — Feature 4.1).
 *
 * Lives in `domain/` and not in the editor because three surfaces consume it:
 * the editor (which validates the 100%), the Performance comparison (which
 * ranks the arms) and whatever persists the campaign. If each computed its own,
 * the campaign total could disagree with the table right below it.
 *
 * Pure.
 */

import { generateUUID } from '../../../utils/helpers';
import type { CampaignStats, Subagent } from '../types';

/**
 * Four and no more, PER AUDIENCE.
 *
 * With two there is already a test; with four each arm takes a quarter of the
 * base, and below that each arm's numbers are too small to decide anything. The
 * limit is statistical, not a screen constraint — and the statistic is the
 * audience's: each audience is its own 100%, so its own test (VOX-301: the
 * add button disappears once an audience holds four).
 */
export const MAX_SUBAGENTS = 4;

export const ZERO_STATS: CampaignStats = {
  audienceSize: 0,
  eligible: 0,
  reached: 0,
  attempts: 0,
  connected: 0,
  rpc: 0,
  conversions: 0,
  voicemail: 0,
  noAnswer: 0,
  minutes: 0,
  cost: 0,
};

/** An arm with no name yet: the positional label is resolved when painted. */
export function emptySubagent(
  skillId: string,
  voice: string,
  share: number,
  groupId: string,
): Subagent {
  return {
    id: generateUUID(),
    groupId,
    name: '',
    skillId,
    voice,
    share,
    stats: { ...ZERO_STATS },
  };
}

/**
 * The arms after the audiences changed, while a campaign is edited in its
 * detail (VOX-317):
 * an audience that has no arm gets one — with the skill and voice of the last
 * arm, or `fallback` — and the arms of a removed audience go with it. Without
 * it, an audience added to a campaign is called by nobody until someone
 * remembers. The same list (same reference) when nothing changed.
 */
export function withOneArmPerGroup(
  subs: Subagent[],
  groups: { id: string }[],
  fallback: { skillId: string; voice: string },
): Subagent[] {
  const ids = new Set(groups.map((group) => group.id));
  const kept = subs.filter((arm) => ids.has(arm.groupId));
  const last = kept[kept.length - 1] ?? subs[subs.length - 1];
  const missing = groups
    .filter((group) => !kept.some((arm) => arm.groupId === group.id))
    .map((group) =>
      emptySubagent(
        last?.skillId || fallback.skillId,
        last?.voice || fallback.voice,
        100,
        group.id,
      ),
    );
  if (missing.length === 0 && kept.length === subs.length) return subs;
  // In the audiences' order: the step draws the arms by audience, so a new one
  // shows inside its own and not at the end of the list.
  const all = [...kept, ...missing];
  return groups.flatMap((group) => all.filter((arm) => arm.groupId === group.id));
}

/** Off doesn't call. Absent or `true` means on, so saved campaigns don't migrate. */
export const isSubagentOn = (sa: Subagent): boolean => sa.enabled !== false;

/** What an unnamed subagent is called: by its position. */
export function subagentLabel(
  sub: Subagent,
  index: number,
  fallback?: (n: number) => string,
): string {
  return sub.name?.trim() || (fallback ? fallback(index + 1) : `Subagent ${index + 1}`);
}

/* ─── Share math ─────────────────────────────────────────────────────────── */

export const sharesTotal = (subs: Subagent[]): number => subs.reduce((n, s) => n + s.share, 0);

/** The groups whose shares don't add up to 100, so the UI can point at them. */
export function groupsNotClosing(subs: Subagent[]): string[] {
  const byGroup = new Map<string, number>();
  for (const sa of subs.filter(isSubagentOn)) {
    byGroup.set(sa.groupId, (byGroup.get(sa.groupId) ?? 0) + sa.share);
  }
  return [...byGroup.entries()].filter(([, total]) => total !== 100).map(([id]) => id);
}

/**
 * Splits `total` into integers that sum to EXACTLY `total`.
 *
 * Largest-remainder method: each weight takes its integer part and the leftover
 * points go to the biggest fractions. Rounding each part on its own is what
 * produces 99% or 101% splits, and a percentage that doesn't close in a control
 * that promises to close itself is worse than having no control.
 */
export function largestRemainder(total: number, weights: number[]): number[] {
  if (weights.length === 0) return [];
  const sum = weights.reduce((a, b) => a + b, 0);
  if (total <= 0 || sum <= 0) return weights.map(() => 0);

  const exact = weights.map((w) => (w * total) / sum);
  const out = exact.map(Math.floor);
  let left = total - out.reduce((a, b) => a + b, 0);

  const byFraction = exact
    .map((v, i) => ({ i, frac: v - Math.floor(v) }))
    // The index breaks ties so two equal fractions always split the same way:
    // without it, the same split could yield two different results.
    .sort((a, b) => b.frac - a.frac || a.i - b.i);

  for (let k = 0; k < byFraction.length && left > 0; k += 1, left -= 1) {
    out[byFraction[k].i] += 1;
  }
  return out;
}

/**
 * Splits evenly and gives the remainder to the first.
 *
 * Three subagents don't divide into integers: 33/33/33 leaves 1% unowned and
 * the total never reaches 100. The remainder goes to the first and is not
 * distributed, because "34/33/33" reads at a glance and "34/33/33 but the
 * second one has 33.34" does not.
 */
export function balanceShares(subs: Subagent[]): Subagent[] {
  if (subs.length === 0) return subs;
  const base = Math.floor(100 / subs.length);
  const rest = 100 - base * subs.length;
  return subs.map((s, i) => ({ ...s, share: i === 0 ? base + rest : base }));
}

/**
 * Moving one percentage takes it from the others; the total never breaks.
 *
 * The split stopped being something you validate and became something that
 * cannot be wrong: before, you could type 50/50/50 and the screen answered with
 * a warning and a button to fix it — a control that let you into an invalid
 * state only to then ask you to leave it.
 *
 * The remainder is split PROPORTIONALLY to what each one had, not evenly and
 * not all to the next one. With two arms the three rules agree; with three, the
 * proportional one is the only one that respects the relationship you had
 * already set between the arms you didn't touch (60/30/10, raising the first to
 * 70, becomes 70/22/8 — not 70/20/10 nor 70/0/30).
 *
 * When the others are all at zero there is no proportion to respect and it
 * splits evenly, which is all that's left.
 *
 * Scoped to ONE audience: pass only the arms of that group, since each audience
 * is its own 100.
 */
export function redistributeShare(subs: Subagent[], id: string, next: number): Subagent[] {
  const target = Math.min(100, Math.max(0, Math.round(next)));
  const others = subs.filter((s) => s.id !== id);
  if (others.length === 0) return subs.map((s) => ({ ...s, share: 100 }));

  const weights = others.map((s) => s.share);
  const flat = weights.reduce((a, b) => a + b, 0) <= 0;
  const shares = largestRemainder(100 - target, flat ? others.map(() => 1) : weights);
  const byId = new Map(others.map((s, i) => [s.id, shares[i]]));

  return subs.map((s) =>
    s.id === id ? { ...s, share: target } : { ...s, share: byId.get(s.id) ?? 0 },
  );
}

/**
 * Brings the split back to 100 preserving the proportions.
 *
 * Used when removing a subagent: its share is spread among the survivors
 * according to what they already had. `balanceShares` would leave them all
 * equal, which erases the split decision just because an arm was deleted.
 */
export function normalizeShares(subs: Subagent[]): Subagent[] {
  if (subs.length === 0) return subs;
  const weights = subs.map((s) => s.share);
  const flat = weights.reduce((a, b) => a + b, 0) <= 0;
  const shares = largestRemainder(100, flat ? subs.map(() => 1) : weights);
  return subs.map((s, i) => ({ ...s, share: shares[i] }));
}

/** The campaign is the SUM of its subagents, not a separate number that can lie. */
export function sumStats(subs: Subagent[]): CampaignStats {
  const out = { ...ZERO_STATS };
  for (const s of subs) {
    for (const k of Object.keys(out) as (keyof CampaignStats)[]) out[k] += s.stats[k];
  }
  out.cost = Math.round(out.cost * 100) / 100;
  return out;
}

/* ─── Name collisions ────────────────────────────────────────────────────── */

/** A name as it READS: without accents, case or extra spaces. */
const readKey = (s: string): string =>
  s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/\s+/g, ' ').trim();

/**
 * The named arms that run the same skill with the same voice as an arm with
 * ANOTHER name (VOX-389) — every arm of the clash, since neither came first.
 *
 * They would be two subagents with one combination: at launch `persistArms`
 * links both to the same catalogue entry, so the campaign and the catalogue
 * would say different things about one subagent. The same name on both is
 * `duplicateNameIds`' case, and an unnamed arm is left out — a blank arm
 * inherits its sibling's skill and voice, and its gate is the missing name.
 */
export function comboClashIds(subs: Subagent[]): Set<string> {
  const byCombo = new Map<string, Subagent[]>();
  subs
    .filter((s) => s.skillId && s.voice && s.name?.trim())
    .forEach((s) => {
      const combo = `${s.skillId}\u0000${s.voice}`;
      byCombo.set(combo, [...(byCombo.get(combo) ?? []), s]);
    });
  const out = new Set<string>();
  for (const group of byCombo.values()) {
    if (new Set(group.map((s) => readKey(s.name))).size > 1) group.forEach((s) => out.add(s.id));
  }
  return out;
}

/**
 * The arms that REPEAT an earlier arm's name.
 *
 * Two arms with the same name break everything that identifies them BY name,
 * which turned out to be nearly everything: the results table writes the name
 * into each contact's row, the subagent filter paints two identical entries
 * that cannot be told apart, and the schema preview lists them as one. The id
 * exists, but it is visible in none of the three: what the user reads is the
 * name.
 *
 * The RESOLVED name is compared, not the typed one. An unnamed arm is called
 * "Subagent 2" by its position, so typing "Subagent 2" into the first one
 * produces the clash even though the two fields say different things — and that
 * is exactly the case nobody sees coming.
 *
 * And it compares without accents, case or extra spaces, because the problem
 * isn't that two strings are equal but that they READ as equal.
 *
 * Campaign-scoped and not audience-scoped: the contact row and the filter span
 * the whole campaign, so two homonyms in different audiences clash just as much.
 */
export function duplicateNameIds(
  subs: Subagent[],
  /** How to name the unnamed ones. Arrives translated. */
  fallbackName: (n: number) => string,
): Set<string> {
  const seen = new Map<string, { id: string; typed: boolean }[]>();
  subs.forEach((sa, i) => {
    const k = readKey(subagentLabel(sa, i, fallbackName));
    seen.set(k, [...(seen.get(k) ?? []), { id: sa.id, typed: Boolean(sa.name?.trim()) }]);
  });

  // Only the REPEATS, not the first one to be called that.
  //
  // Who is "first" is not list order but who TYPED the name. An unnamed arm is
  // called "Subagent 2" by position: it did not choose to be called that and
  // cannot choose otherwise, the field is empty. So if somebody types
  // "Subagent 2" into the arm above, the one repeating is that one — the other
  // way round, the error landed on an empty field asking to change a name
  // nobody wrote.
  const out = new Set<string>();
  for (const group of seen.values()) {
    const ordered = [...group].sort((a, b) => Number(a.typed) - Number(b.typed));
    ordered.slice(1).forEach((x) => out.add(x.id));
  }
  return out;
}

/* ─── Audience-scoped operations ─────────────────────────────────────────── */
//
// Each audience is its own 100%: every operation below touches the arms of ONE
// `groupId` and leaves the rest of the list exactly as it was. They live here
// so the editor holds no share arithmetic of its own (VOX-301).

/** The arms of one audience, in list order. */
export const armsOfGroup = (subs: Subagent[], groupId: string): Subagent[] =>
  subs.filter((s) => s.groupId === groupId);

/** Whether this audience still has room for another arm. */
export const canAddToGroup = (subs: Subagent[], groupId: string): boolean =>
  armsOfGroup(subs, groupId).length < MAX_SUBAGENTS;

/** Replaces the arms of `groupId` with their re-computed versions, by id. */
function mergeGroup(subs: Subagent[], next: Subagent[]): Subagent[] {
  const byId = new Map(next.map((s) => [s.id, s]));
  return subs.map((s) => byId.get(s.id) ?? s);
}

/**
 * Appends an arm and re-splits ITS audience evenly.
 *
 * A new arm at 0% calls nobody, so adding it would do nothing visible and the
 * percentages would have to be moved by hand for it to exist. The other
 * audiences have nothing to do with this 100 and are not touched. A full
 * audience returns the list unchanged.
 */
export function addToGroup(subs: Subagent[], arm: Subagent): Subagent[] {
  if (!canAddToGroup(subs, arm.groupId)) return subs;
  const next = [...subs, arm];
  return mergeGroup(next, balanceShares(armsOfGroup(next, arm.groupId)));
}

/**
 * Moves one arm's share, taking it from its SIBLINGS in the same audience.
 *
 * The split cannot be left wrong: typing 70 in a 50/50 audience gives 70/30,
 * never a 120% that a warning then asks you to fix.
 */
export function redistributeInGroup(subs: Subagent[], id: string, next: number): Subagent[] {
  const target = subs.find((s) => s.id === id);
  if (!target) return subs;
  return mergeGroup(subs, redistributeShare(armsOfGroup(subs, target.groupId), id, next));
}

/**
 * Removes an arm and closes its audience back at 100.
 *
 * Proportionally (`normalizeShares`), not evenly: removing the third of a
 * 60/30/10 split leaves 67/33, because deleting an arm should not erase the
 * decision already made between the ones that stayed.
 */
export function removeFromGroup(subs: Subagent[], id: string): Subagent[] {
  const target = subs.find((s) => s.id === id);
  if (!target) return subs;
  const rest = subs.filter((s) => s.id !== id);
  return mergeGroup(rest, normalizeShares(armsOfGroup(rest, target.groupId)));
}

/**
 * How many of an audience's `eligible` contacts each of its ON arms takes.
 *
 * By share, with the largest remainder so the counts add up to `eligible`
 * exactly. Off arms take nobody — their slice goes to the ones still calling,
 * weighted by their shares, which is how the backend assigns too
 * (`campaignAssignment`). Arms at 0 across the board split evenly.
 */
export function armContacts(groupArms: Subagent[], eligible: number): Map<string, number> {
  const on = groupArms.filter(isSubagentOn);
  const weights = on.map((s) => s.share);
  const flat = weights.reduce((a, b) => a + b, 0) <= 0;
  const counts = largestRemainder(Math.max(0, eligible), flat ? on.map(() => 1) : weights);
  const out = new Map(groupArms.map((s) => [s.id, 0]));
  on.forEach((s, i) => out.set(s.id, counts[i]));
  return out;
}

/** What pausing an arm does, before it is paused — the modal says it out loud. */
export interface PauseConsequences {
  /** No other ON arm in the campaign runs this skill: it leaves execution. */
  skillStops: boolean;
  /** ON siblings in its audience that take over its contacts (0 = nobody does). */
  siblings: number;
}

/**
 * The consequences of pausing `id`, which differ by arm.
 *
 * The skill is the CAMPAIGN's; the split is the AUDIENCE's. Mixing them was the
 * same error as summing every group's shares against 100: pausing an arm of
 * one audience redistributes nothing to another. And an arm that is the only
 * one calling its audience redistributes nothing at all — it leaves the
 * audience with nobody, the gravest of the three and the least visible.
 */
export function pauseConsequences(subs: Subagent[], id: string): PauseConsequences {
  const target = subs.find((s) => s.id === id);
  if (!target) return { skillStops: false, siblings: 0 };
  const othersOn = subs.filter((s) => s.id !== id && isSubagentOn(s));
  return {
    skillStops: othersOn.every((s) => s.skillId !== target.skillId),
    siblings: othersOn.filter((s) => s.groupId === target.groupId).length,
  };
}
